'use client'

import Image from 'next/image'
import React, { useRef } from 'react'

import { usePDP } from '@src/hooks/usePDP'
import { useLocale } from '@src/i18n/LocaleProvider'
import { getImage } from '@src/utils/product.util'
import { cn } from '@src/utils/tailwind'

import type { CarouselHandlers } from './modules/ProductCarousel/ProductCarousel'
import { ProductCarousel } from './modules/ProductCarousel/ProductCarousel'

export default function ProductGallery() {
  const imagesRef = useRef<any[]>([])
  const { locale } = useLocale('common')
  const { viewModel } = usePDP()
  const carouselRef = useRef<CarouselHandlers>(null)
  const variantAssets = viewModel?.selectedVariant?.assets ?? []
  const productAssets = viewModel?.product?.assets ?? []
  const images = [...variantAssets, ...productAssets].map((asset) => getImage(asset, locale.code))

  return (
    <div className="mb-4 lg:mb-0">
      <div className="lg:hidden">
        <ProductCarousel
          slidesPerView={1}
          handlersRef={carouselRef}
          dots={{ color: '#ccc', activeColor: '#000' }}
          slides={images.map((image, index) => (
            <div className="flex justify-center" key={`${viewModel?.selectedVariant.sku}-${index}`}>
              <Image
                src={image.url}
                alt={image.alt}
                width={332}
                height={332}
                className="pointer-events-none max-w-full object-contain"
              />
            </div>
          ))}
        />
      </div>
      <div className="mb-6 hidden lg:flex lg:gap-6">
        <div
          className={cn(
            'no-scrollbar sticky top-0 w-32 shrink-0 self-start overflow-scroll pt-6 lg:max-h-[calc(100vh-10px)] lg:pt-20',
            {
              hidden: images.length <= 1,
            }
          )}
        >
          <div>
            <div className="flex cursor-pointer flex-col gap-2">
              {images.map((image, index) => (
                <div
                  role="presentation"
                  className="size-[100px]"
                  key={`${viewModel?.selectedVariant.sku}-${index}`}
                  onClick={() =>
                    imagesRef?.current[index].scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                      inline: 'nearest',
                    })
                  }
                >
                  <Image src={image.url} alt={image.alt} height={100} width={100}></Image>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* negative margin needed so all images are all aligned */}
        <div className="-mt-14 flex flex-col">
          {images.map((image, index) => (
            <div
              key={`${viewModel?.selectedVariant.sku}-${index}`}
              ref={(el: any) => {
                imagesRef.current[index] = el
              }}
              className="-mb-20 pt-24 last:-mb-6"
            >
              <Image src={image.url} alt={image.alt} width={662} height={662}></Image>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
